import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../../lib/useReducedMotion';

/**
 * Fade-and-rise on first entry into the viewport. Once only, then the observer
 * is disconnected and the element is left alone for the rest of the session.
 *
 * The site had reveals before, via framer-motion, and they were removed with the
 * library — see lib/useReducedMotion. This is the same effect for none of the
 * weight: one IntersectionObserver per block, an attribute flip, and two
 * compositor-only properties (see `[data-reveal]` in index.css). No scroll
 * handler, no rAF loop, nothing that runs per frame.
 *
 * Spread the returned props onto the element and give it the ref. Use this
 * rather than <Reveal> wherever an extra <div> would break the parent's layout
 * or its semantics — a grid item, or an <li> inside an <ol>.
 */
export function useReveal<T extends HTMLElement>(delay = 0) {
  const ref = useRef<T>(null);
  const reduce = usePrefersReducedMotion();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;

    // No observer, or the visitor asked for less motion: skip straight to the
    // resting state. The CSS neutralises the transition under reduced motion, so
    // this is an instant appearance rather than a fast animation.
    if (reduce || typeof IntersectionObserver !== 'function') {
      setShown(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setShown(true);
      },
      // A little inside the bottom edge, so the block is committed to being on
      // screen before it starts — not animating in a sliver at the fold.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [reduce, shown]);

  return {
    ref,
    'data-reveal': shown ? 'in' : '',
    style: delay && !reduce ? { transitionDelay: `${delay}ms` } : undefined,
  };
}

type RevealProps = {
  children: React.ReactNode;
  /** Classes for the wrapper. It is a plain block — give it the margin. */
  className?: string;
  /** Milliseconds. Used to stagger a block against the head above it. */
  delay?: number;
};

/** The common case: wrap a block-level region in a <div> that reveals. */
const Reveal: React.FC<RevealProps> = ({ children, className = '', delay = 0 }) => {
  const reveal = useReveal<HTMLDivElement>(delay);

  return (
    <div {...reveal} className={className}>
      {children}
    </div>
  );
};

export default Reveal;
